import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Checkbox, FormControlLabel, Grid, MenuItem, Chip as MuiChip, TablePagination, TextField, Typography } from '@mui/material'
import { GreenText, Title } from 'components'
import { useLayout } from 'context'
import { createOutboundTrip, getOutboundTrips as getOutboundTripsApi } from 'controllers/outboundTrips/index'
import { OutboundTripListDto, OutboundTripQueryParams } from 'dtos/outboundTrips'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateMoment, useDebounce } from 'utils'
import AddEditOutboundTripDialog from './AddEditOutboundTripDialog'
import OutboundTripListCard from './OutboundTripListCard'

const defaultQueryParams: OutboundTripQueryParams = {
  page: 0,
  pageSize: 10,
  includeSignedBOL: false
}

const sessionStorageKey = 'outboundTrips:queryParams'

export default function OutboundTrips() {
  const { setIsLayoutLoading } = useLayout()
  const navigate = useNavigate()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isGetting, setIsGetting] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [outboundTrips, setOutboundTrips] = useState<OutboundTripListDto[]>([])
  const [queryParams, setQueryParams] = useState<OutboundTripQueryParams>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultQueryParams
  )
  const [totalCount, setTotalCount] = useState<number>(0)

  const getOutboundTrips = useDebounce((queryParams: OutboundTripQueryParams) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(queryParams))

    setIsGetting(true)
    getOutboundTripsApi(queryParams)
      .then(res => {
        setOutboundTrips(res.value)
        setTotalCount(res.totalCount ?? 0)
      })
      .finally(() => setIsGetting(false))
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    getOutboundTrips(queryParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, page: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.pageSize])

  useEffect(() => {
    setIsLayoutLoading(isGetting)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetting])

  return (
    <>
      <Title>Outbound Trips</Title>

      <AddEditOutboundTripDialog
        initialValues={{
          outboundTripGuid: undefined,
          outboundTripNumber: '',
          outboundTripDate: '',
          outboundTransporterCompany: {
            companyGuid: '',
            name: '',
            dotNumber: undefined
          }
        }}
        isLoading={isSaving}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSaving(true)
          createOutboundTrip(values)
            .then(() => {
              enqueueSnackbar('Outbound Trip Created Successfully!', { variant: 'success' })
              getOutboundTrips(queryParams)
              setDialogOpen(false)
            })
            .finally(() => setIsSaving(false))
        }}
        open={dialogOpen}
      />

      <Grid container spacing={1} alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Outbound Trips ({totalCount})
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item container spacing={1} xs={12} sm={true} alignItems='center'>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label='Filter Field'
                      onChange={e => {
                        setFilterField(e.target.value)
                        setFilterValue('')
                      }}
                      select
                      size='small'
                      sx={{ minWidth: 200 }}
                      value={filterField || ''}
                    >
                      <MenuItem value='outboundTripNumber'>Outbound Trip #</MenuItem>

                      <MenuItem value='outboundTripDate'>Outbound Trip Date</MenuItem>

                      <MenuItem value='outboundTransporterCompany'>Outbound Transporter Company</MenuItem>

                      <MenuItem value='outboundTransporterCompanyDotNumber'>DOT #</MenuItem>

                      <MenuItem value='destinationCompany'>Outbound Trip Destination</MenuItem>

                      <MenuItem value='destinationAddress'>Outbound Trip Destination Address</MenuItem>
                    </TextField>
                  </Grid>

                  {[
                    '',
                    'outboundTripNumber',
                    'outboundTransporterCompany',
                    'outboundTransporterCompanyDotNumber',
                    'destinationCompany',
                    'destinationAddress'
                  ].includes(filterField) && (
                    <Grid item xs={12} sm={2}>
                      <TextField
                        disabled={!filterField}
                        label='Filter Value'
                        onChange={e => setFilterValue(e.target.value)}
                        size='small'
                        type={filterField === 'year' ? 'number' : 'text'}
                        value={filterValue || ''}
                      />
                    </Grid>
                  )}

                  {['outboundTripDate'].includes(filterField) && (
                    <>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                          label='Filter Date From'
                          onChange={e => {
                            setFilterValueFrom(e.target.value)
                          }}
                          type='date'
                          value={filterValueFrom}
                        />
                      </Grid>

                      <Grid item xs={12} sm={2}>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                          label='Filter Date To'
                          onChange={e => {
                            setFilterValueTo(e.target.value)
                          }}
                          type='date'
                          value={filterValueTo}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item>
                    <Button
                      color='inherit'
                      disabled={!filterField || (!filterValue && !(filterValueFrom && filterValueTo))}
                      size='medium'
                      variant='contained'
                      onClick={() => {
                        if (filterField === 'outboundTripDate') {
                          setQueryParams({
                            ...queryParams,
                            outboundTripDateFrom: filterValueFrom,
                            outboundTripDateTo: filterValueTo
                          })
                        } else {
                          setQueryParams({ ...queryParams, [filterField]: filterValue })
                        }

                        setFilterField('')
                        setFilterValue('')
                        setFilterValueFrom('')
                        setFilterValueTo('')
                      }}
                    >
                      APPLY FILTER
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      color='secondary'
                      disabled={
                        !Boolean(
                          queryParams.outboundTripNumber ||
                            queryParams.outboundTripDateFrom ||
                            queryParams.outboundTripDateTo ||
                            queryParams.outboundTransporterCompany ||
                            queryParams.outboundTransporterCompanyDotNumber ||
                            queryParams.destinationCompany ||
                            queryParams.destinationAddress ||
                            queryParams.includeSignedBOL
                        )
                      }
                      size='medium'
                      variant='text'
                      onClick={() => {
                        setQueryParams({
                          ...queryParams,
                          outboundTripNumber: undefined,
                          outboundTripDateFrom: undefined,
                          outboundTripDateTo: undefined,
                          outboundTransporterCompany: undefined,
                          outboundTransporterCompanyDotNumber: undefined,
                          destinationCompany: undefined,
                          destinationAddress: undefined,
                          includeSignedBOL: false
                        })
                      }}
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>

                  <Grid item>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={queryParams.includeSignedBOL}
                          onChange={() => setQueryParams({ ...queryParams, includeSignedBOL: !queryParams.includeSignedBOL })}
                          color='primary'
                        />
                      }
                      label='Include Outbound Trips with Signed BOL Uploaded'
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto' container alignItems='center'>
                  <Grid item>
                    <Button color='primary' onClick={() => setDialogOpen(true)} variant='contained'>
                      ADD OUTBOUND TRIP
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {queryParams.outboundTripNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Outbound Trip # contains "${queryParams.outboundTripNumber}"`}
                        onDelete={() => setQueryParams({ ...queryParams, outboundTripNumber: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.outboundTripDateFrom && queryParams.outboundTripDateTo && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Outbound Trip Date from "${formatDateMoment(queryParams.outboundTripDateFrom)}" to "${formatDateMoment(
                          queryParams.outboundTripDateTo
                        )}"`}
                        onDelete={() => setQueryParams({ ...queryParams, outboundTripDateFrom: undefined, outboundTripDateTo: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.outboundTransporterCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Outbound Transporter Company contains "${queryParams.outboundTransporterCompany}"`}
                        onDelete={() => setQueryParams({ ...queryParams, outboundTransporterCompany: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.outboundTransporterCompanyDotNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`DOT # contains "${queryParams.outboundTransporterCompanyDotNumber}"`}
                        onDelete={() => setQueryParams({ ...queryParams, outboundTransporterCompanyDotNumber: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.destinationCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Outbound Trip Destination contains "${queryParams.destinationCompany}"`}
                        onDelete={() => setQueryParams({ ...queryParams, destinationCompany: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.destinationAddress && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Outbound Trip Destination Address contains "${queryParams.destinationAddress}"`}
                        onDelete={() => setQueryParams({ ...queryParams, destinationAddress: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {outboundTrips.map(outboundTrip => (
          <Grid item xs={12} key={outboundTrip.outboundTripGuid}>
            <OutboundTripListCard outboundTrip={outboundTrip}>
              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    navigate(`/outbound-trips/${encodeURIComponent(outboundTrip.outboundTripGuid)}`)
                  }}
                >
                  MANAGE OUTBOUND TRIP
                </Button>
              </Grid>
            </OutboundTripListCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <GreenText>
            {`**Outbound Trips List Screen:**

Outbound Trips are sorted from Newest to Oldest.
 
**Filter – Include Outbound Trips with Signed BOL Uploaded:**

This checkbox is unchecked by default.

When checked, Outbound Trips that have a populated Signed BOL Uploaded date will be included in the list.

**Add Outbound Trip Button:**

Clicking this button will open the Add/Edit Outbound Trip Dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:
 
   - Outbound Trip #
   - Outbound Trip Date
   - Outbound Transporter Company
   - DOT #
   - Outbound Trip Destination
   - Outbound Trip Destination Address

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Outbound Trip #: Displays a free-type filter field.
   - Outbound Trip Date: Displays Filter Date From and Filter Date To.
   - Outbound Transporter Company: Displays a free-type filter field.
   - DOT #: Displays a free-type filter field.
   - Outbound Trip Destination: Displays a free-type filter field.
   - Outbound Trip Destination Address: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Locked Status Indicator:**

If an Outbound Trip is locked, a status chip will be displayed on the card for visual identification.

**Manage Outbound Trip Button:**

Clicking this button opens the Outbound Trip Details screen for the selected Outbound Trip.

**Last 6 Of VIN Column:**

Displays the last six characters of the vehicle’s VIN.

**Importer Stock#:**

Displays a sequential number starting at 20000, followed by a three-character Exporter Code or Buyer Code (if the vehicle has a buyer).

This field is populated only when the vehicle is locked.

**Year Column:**

Displays the vehicle’s year from Vehicle Details.

**Make Column:**

Displays the vehicle’s make from Vehicle Details.

**Model Column:**

Displays the vehicle’s model from Vehicle Details.

**Exporter/Buyer Column:**

Displays the Exporter/Buyer for the vehicle.

**Outbound Trip # Label:**

Displays the unique identifier of the Outbound Trip to which the vehicle is assigned.

**Outbound Trip Date Label:**

Displays the scheduled date of the Outbound Trip.

**Outbound Transporter Company Label:**

Displays the company name for the Outbound Transporter Company.

**DOT # Label:**

Displays the DOT Number for the Outbound Trip.

**Outbound Trip Destination Label:**

Displays the Outbound Trip Destination for the Outbound Trip

**Outbound Trip Destination Address Label:**

Displays the Outbound Trip Destination Address for the Outbound Trip.

**Drivers License Photo Uploaded Label:**

Displays either “Yes” or “No” for if the Drivers License Photo has been uploaded for the Outbound Trip.

**Signed BOL Uploaded Label:**

Displays the date that the Signed BOL has been uploaded for the Outbound Trip.

If no Signed BOL has been uploaded for the Outbound Trip a “-“ will be displayed as the value.`}
          </GreenText>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => setQueryParams({ ...queryParams, page })}
            onRowsPerPageChange={e => setQueryParams({ ...queryParams, pageSize: e.target.value as unknown as number })}
            page={queryParams.page}
            rowsPerPage={queryParams.pageSize}
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
